// Consent management utility
export const CONSENT_STORAGE_KEY = 'cookie-consent';

export const defaultConsent = {
  necessary: true,
  analytics: false,
  advertising: false
};

// Initialize consent mode on page load
export const initializeConsent = () => {
  const preferences = getConsentPreferences();
  if (preferences) {
    // Apply existing consent preferences
    updateGoogleConsent(preferences.analytics, preferences.advertising);
  } else {
    // No consent given yet, ensure default denied state is applied
    updateGoogleConsent(false, false);
  }
};

export const getConsentPreferences = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error reading consent preferences:', error);
    }
    return null;
  }
};

export const setConsentPreferences = (preferences) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(preferences));
    updateGoogleConsent(preferences.analytics, preferences.advertising);
    // Fire consent update event for tracking
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('event', 'consent_update', {
        'event_category': 'consent',
        'analytics_consent': preferences.analytics ? 'granted' : 'denied',
        'ads_consent': preferences.advertising ? 'granted' : 'denied'
      });
    }
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error saving consent preferences:', error);
    }
    return false;
  }
};

export const updateGoogleConsent = (analyticsAccepted, advertisingAccepted) => {
  if (typeof window !== 'undefined' && typeof window.gtag !== 'undefined') {
    // Add a small delay to ensure dataLayer is ready
    requestAnimationFrame(() => {
      window.gtag('consent', 'update', {
        'analytics_storage': analyticsAccepted ? 'granted' : 'denied',
        'ad_storage': advertisingAccepted ? 'granted' : 'denied',
        'ad_user_data': advertisingAccepted ? 'granted' : 'denied',
        'ad_personalization': advertisingAccepted ? 'granted' : 'denied',
        // Add these for better EEA compliance
        'functionality_storage': 'granted',
        'security_storage': 'granted'
      });
      
      // Fire a custom event to confirm consent update
      window.gtag('event', 'consent_mode_update', {
        'event_category': 'consent',
        'analytics_storage': analyticsAccepted ? 'granted' : 'denied',
        'ad_storage': advertisingAccepted ? 'granted' : 'denied'
      });
    });
  }
};

export const hasConsentBeenGiven = () => {
  return getConsentPreferences() !== null;
};

export const acceptAllConsent = () => {
  const preferences = {
    necessary: true,
    analytics: true,
    advertising: true
  };
  return setConsentPreferences(preferences);
};

export const rejectAllConsent = () => {
  const preferences = {
    necessary: true,
    analytics: false,
    advertising: false
  };
  return setConsentPreferences(preferences);
};

export const clearConsentPreferences = () => {
  try {
    localStorage.removeItem(CONSENT_STORAGE_KEY);
    // Reset to default denied state
    updateGoogleConsent(false, false);
    return true;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('Error clearing consent preferences:', error);
    }
    return false;
  }
};

// Debug function to check consent status
export const debugConsentStatus = () => {
  const preferences = getConsentPreferences();
  const hasConsent = hasConsentBeenGiven();
  
  // Only log in development mode
  if (process.env.NODE_ENV === 'development') {
    console.group('🍪 Consent Debug Information');
    console.log('Has consent been given:', hasConsent);
    console.log('Current preferences:', preferences);
    console.log('GTM dataLayer:', window.dataLayer || 'Not found');
    console.log('gtag function:', typeof window.gtag);
    
    if (window.dataLayer) {
      const consentEvents = window.dataLayer.filter(item => 
        item.event === 'consent' || item[0] === 'consent'
      );
      console.log('Consent events in dataLayer:', consentEvents);
    }
    
    console.groupEnd();
  }
  
  return {
    hasConsent,
    preferences,
    dataLayerAvailable: !!window.dataLayer,
    gtagAvailable: typeof window.gtag !== 'undefined'
  };
}; 